import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import type { AgBarSeriesOptions } from 'ag-charts-enterprise';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { useMemo } from 'react';

import DonutChart from '@/components/charts/DonutChart/DonutChart';
import StackedBarChart from '@/components/charts/StackedBarChart';
import { REPORTS_COLORS } from '@/constants/chartcolors.constants';
import REPORT_PARAGRAPHS from '@/constants/reportparagraph.constants';
import ReportParagraph from '@/features/ReportsTemplateParagraph/ReportsParagraph';
import ReportsTemplateSectionTitle from '@/features/ReportsTemplateSectionTitle/ReportsTemplateSectionTitle';

import {
  reportsEndDateAtom,
  reportsTimeFrameAtom
} from '../../ReportsTemplate.hooks';
import {
  getURLTimeframeData,
  interpolateColorHex
} from '../../ReportsTemplate.utils';
import useStyles from '../ReportsPages.styles';
import { useFetchReportsEventsData } from './EventsPages.hooks';

const EventsPage2 = () => {
  const { classes } = useStyles();
  const [diff] = useAtom(reportsTimeFrameAtom);
  const [endDate] = useAtom(reportsEndDateAtom);

  const { data: logEventsByVendor } = useFetchReportsEventsData({
    groupByField: 'vendor',
    eventType: 'log'
  });
  const { data: logEventsByProduct } = useFetchReportsEventsData({
    groupByField: 'product',
    eventType: 'log'
  });

  const { data: logEventsByLogForwarder } = useFetchReportsEventsData({
    groupByField: 'log_forwarder',
    eventType: 'log'
  });

  const singleDayVendorEvents = getURLTimeframeData(
    logEventsByVendor ?? [],
    'date'
  );
  const singleDayProductEvents = getURLTimeframeData(
    logEventsByProduct ?? [],
    'date'
  );

  const timeFrameCorrectedVendorEvents =
    diff < 7 ? singleDayVendorEvents : logEventsByVendor;
  const timeFrameCorrectedProductEvents =
    diff < 7 ? singleDayProductEvents : logEventsByProduct;

  // Log Events by Product
  const logEventsProductData = useMemo(() => {
    const groupTopData = (timeFrameCorrectedProductEvents ?? []).reduce(
      (acc, { count, field_to_group_by }) => {
        if (!acc[field_to_group_by]) {
          acc[field_to_group_by] = { name: field_to_group_by, count: 0 };
        }
        acc[field_to_group_by].count += count;
        return acc;
      },
      {} as Record<string, { name: string; count: number }>
    );

    const sortedTop = (
      Object.values(groupTopData) as Array<{ name: string; count: number }>
    )
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return sortedTop.map((item, index, array) => ({
      ...item,
      color: interpolateColorHex(
        REPORTS_COLORS.LOG_EVENTS,
        REPORTS_COLORS.NETWORK_EVENTS,
        index / Math.max(1, array.length - 1)
      )
    }));
  }, [timeFrameCorrectedProductEvents]);

  // Log Events by Vendor
  const logEventsVendorData = useMemo(() => {
    const groupTopData = (timeFrameCorrectedVendorEvents ?? []).reduce(
      (acc, { count, field_to_group_by }) => {
        if (!acc[field_to_group_by]) {
          acc[field_to_group_by] = { name: field_to_group_by, count: 0 };
        }
        acc[field_to_group_by].count += count;
        return acc;
      },
      {} as Record<string, { name: string; count: number }>
    );

    const sortedTop = (
      Object.values(groupTopData) as Array<{ name: string; count: number }>
    )
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return sortedTop.map((item, index, array) => ({
      ...item,
      color: interpolateColorHex(
        REPORTS_COLORS.LOG_EVENTS,
        REPORTS_COLORS.NETWORK_EVENTS,
        index / Math.max(1, array.length - 1)
      )
    }));
  }, [timeFrameCorrectedVendorEvents]);

  // Log Events by Log Forwarder - for graph
  const logEventsByDateByLogForwarder = useMemo(() => {
    if (!logEventsByLogForwarder?.length) return [];

    const daysToGenerate = Math.max(diff, 7);

    const dateRange = Array.from({ length: daysToGenerate }, (_, i) =>
      dayjs(endDate)
        .subtract(daysToGenerate - 1 - i, 'day')
        .format('YYYY-MM-DD')
    );

    const forwarderSet = new Set<string>();
    const dataLookup = new Map<string, Map<string, number>>();

    logEventsByLogForwarder.forEach((item) => {
      const dateKey = item.date;
      forwarderSet.add(item.field_to_group_by);

      let dateEntry = dataLookup.get(dateKey);
      if (!dateEntry) {
        dateEntry = new Map();
        dataLookup.set(dateKey, dateEntry);
      }
      dateEntry.set(item.field_to_group_by, item.count);
    });

    const uniqueForwarders = Array.from(forwarderSet);

    return dateRange.map((dateStr) => {
      const dateObj: Record<string, Date | number> = {
        date: dayjs(dateStr).toDate()
      };

      uniqueForwarders.forEach((forwarder) => {
        dateObj[forwarder] = dataLookup.get(dateStr)?.get(forwarder) ?? 0;
      });

      return dateObj;
    });
  }, [logEventsByLogForwarder, diff, endDate]);

  const logForwarderSeries: AgBarSeriesOptions[] = useMemo(() => {
    if (!logEventsByLogForwarder?.length) return [];

    const uniqueForwarders = [
      ...new Set(logEventsByLogForwarder.map((item) => item.field_to_group_by))
    ];

    const colorFactor = Math.max(1, uniqueForwarders.length - 1);

    return uniqueForwarders.map((forwarder, index) => ({
      type: 'bar' as const,
      xKey: 'date' as const,
      yKey: forwarder,
      yName: forwarder,
      stacked: true,
      fill: interpolateColorHex(
        REPORTS_COLORS.LOG_EVENTS,
        REPORTS_COLORS.NETWORK_EVENTS,
        index / colorFactor
      )
    }));
  }, [logEventsByLogForwarder]);

  return (
    <div className={classes.reportsPageContainer}>
      <ReportsTemplateSectionTitle>Log Events</ReportsTemplateSectionTitle>
      <Box sx={{ flexDirection: 'row', display: 'flex', gap: 2 }}>
        <Box sx={{ width: '49%' }}>
          <div data-testid="log-events-by-vendor" style={{ height: 200 }}>
            <Typography className={classes.reportsPreviewSubTitle}>
              TOP LOG EVENTS BY VENDOR
            </Typography>
            <DonutChart data={logEventsVendorData} />
          </div>
          <ReportParagraph fontSize="12px">
            {REPORT_PARAGRAPHS.logEventsByVendorDescription}
          </ReportParagraph>
        </Box>
        <div style={{ width: '2%' }}>
          <Box
            sx={{
              backgroundColor: '#D1E2D5',
              width: 2,
              height: '100%'
            }}
          />
        </div>
        <Box sx={{ width: '49%' }}>
          <div data-testid="log-events-by-product" style={{ height: 200 }}>
            <Typography className={classes.reportsPreviewSubTitle}>
              TOP LOG EVENTS BY PRODUCT
            </Typography>
            <DonutChart data={logEventsProductData} />
          </div>
          <ReportParagraph fontSize="12px">
            {REPORT_PARAGRAPHS.logEventsByProductDescription}
          </ReportParagraph>
        </Box>
      </Box>
      <div
        style={{ height: 430, width: '100%' }}
        className={classes.reportsChartContainer}
        data-testid="log-events-by-log-forwarder"
      >
        <Typography className={classes.reportsPreviewSubTitle}>
          Events Per Day By Log Forwarder
        </Typography>
        <StackedBarChart
          data={logEventsByDateByLogForwarder}
          series={logForwarderSeries}
          outlined
        />
      </div>
      <ReportParagraph fontSize="12px">
        {REPORT_PARAGRAPHS.eventsPerDayByLogForwarderDescription}
      </ReportParagraph>
    </div>
  );
};

export default EventsPage2;
